import React from "react";
import {
  Box,
  Flex,
  Label as BaseLabel,
  Radio,
  Span,
  styled,
  themeGet,
} from "@catchoftheday/cg-components";
import { ClubOptionFragment } from "@common-vendors/generated/graphql";
import { formatPrice } from "@cart-app/utilities";

type PlanOptionRadioProps = {
  /** Club Catch subscription option/plan */
  clubSubscriptionOption: ClubOptionFragment;
  /** callback when an option is selected, mainly to handle keyboard event */
  onChange: (value: string) => void;
  /** flag to indicate that this option is selected */
  selected?: boolean;
  /** flag to indicate that this option is disabled */
  disabled?: boolean;
};

const Label = styled(BaseLabel)`
  &:hover {
    & > * {
      background-color: rgba(156, 51, 176, 0.1);
      border: 2px solid ${themeGet("colors.brand.clubCatch")};
    }
  }

  &:focus-visible {
    border: none;
    outline: none;

    & > * {
      background-color: rgba(74, 17, 89, 0.2);
      border: 2px solid ${themeGet("colors.brand.clubCatch")};
    }
  }

  &:active {
    & > * {
      background-color: rgba(74, 17, 89, 0.2);
      box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.25);
    }
  }
`;

export const PlanOptionRadio = ({
  clubSubscriptionOption,
  onChange,
  selected,
  disabled,
}: PlanOptionRadioProps) => {
  const { interval, price } = clubSubscriptionOption;
  const { amount } = price;
  const intervalText = interval.toLowerCase();
  return (
    <Label
      key={`input-radio-${clubSubscriptionOption.id}`}
      htmlFor={`input-radio-${clubSubscriptionOption.id}`}
      tabIndex={0}
      width="50%"
      maxWidth="200px"
      aria-label={`Club Catch ${
        clubSubscriptionOption.interval
      } subscription plan at ${formatPrice(
        clubSubscriptionOption.price.amount
      )} per ${
        clubSubscriptionOption.interval === "MONTHLY" ? "month" : "year"
      }`}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !disabled) {
          onChange(clubSubscriptionOption.id);
        }
      }}
    >
      <Flex
        height="116px"
        borderStyle="solid"
        borderColor={
          disabled
            ? "ui.borderColor"
            : selected
            ? "brand.clubCatch"
            : "ui.borderColor"
        }
        borderWidth="2px"
        position="relative"
        boxShadow={selected ? "box" : undefined}
        backgroundColor={disabled ? "ui.borderColor" : undefined}
        borderRadius="default"
        mx="8px"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        cursor={disabled ? "not-allowed" : "pointer"}
      >
        <Box>
          <Span fontSize="lg" fontWeight="semibold">
            {formatPrice(
              clubSubscriptionOption.interval === "YEARLY"
                ? amount / 12
                : amount
            )}
          </Span>
          <Span fontSize="sm">/mo</Span>
        </Box>
        <Span
          fontSize="base"
          color={disabled ? "textSecondary" : "brand.clubCatch"}
          fontWeight="semibold"
          textTransform="capitalize"
        >
          {intervalText}
        </Span>
        <Span>
          {interval === "MONTHLY"
            ? `Billed ${intervalText}, flexible`
            : `Billed ${intervalText} (${formatPrice(amount)})`}
        </Span>
        {clubSubscriptionOption.interval === "YEARLY" && (
          <Box
            backgroundColor={
              disabled ? "textTertiary" : "brand.clubCatchSecondary"
            }
            color={disabled ? "textWhite" : "brand.clubCatch"}
            position="absolute"
            right="-2px"
            top="-2px"
            borderTopRightRadius="default"
            borderBottomLeftRadius="default"
            padding="2px 4px"
          >
            <Span
              fontSize="xs"
              display="block"
              textAlign="center"
              fontWeight="semibold"
            >
              Best Value
            </Span>
          </Box>
        )}
      </Flex>
      <Box display="none">
        <Radio
          id={`input-radio-${clubSubscriptionOption.id}`}
          value={clubSubscriptionOption.id}
          disabled={disabled}
        />
      </Box>
    </Label>
  );
};
